import React, { useState, useEffect } from 'react';
import { Database, RefreshCw, Download, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { getBackups, triggerBackup, triggerAllBackups, getSchools } from '../api';

export default function Backups() {
  const [backups, setBackups] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [loading, setLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState('');

  useEffect(() => {
    getSchools().then(r => setSchools(r.data.results || r.data));
    loadBackups();
  }, []);

  const loadBackups = (schoolId) => {
    setLoading(true);
    getBackups(schoolId || selectedSchool)
      .then(r => setBackups(r.data.results || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleTrigger = async (id) => {
    setTriggerLoading(id);
    try {
      await triggerBackup(id);
      setTimeout(() => loadBackups(), 2000);
    } catch (e) { console.error(e); }
    finally { setTriggerLoading(''); }
  };

  const handleTriggerAll = async () => {
    setTriggerLoading('all');
    try {
      await triggerAllBackups();
      setTimeout(() => loadBackups(), 3000);
    } catch (e) { console.error(e); }
    finally { setTriggerLoading(''); }
  };

  const handleSchoolFilter = (e) => {
    setSelectedSchool(e.target.value);
    loadBackups(e.target.value);
  };

  return (
    <div>
      <div className="page-header">
        <h2>Backups</h2>
        <p>Database backup management for all schools</p>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card green">
          <div className="stat-icon green"><CheckCircle size={22} /></div>
          <div className="stat-label">Successful</div>
          <div className="stat-value green">{backups.filter(b => b.status === 'success').length}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-icon red"><AlertTriangle size={22} /></div>
          <div className="stat-label">Failed</div>
          <div className="stat-value red">{backups.filter(b => b.status === 'failed').length}</div>
        </div>
        <div className="stat-card accent">
          <div className="stat-icon accent"><Database size={22} /></div>
          <div className="stat-label">Total Backups</div>
          <div className="stat-value accent">{backups.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3><Database size={18} /> Backup History</h3>
            <select
              className="form-select"
              value={selectedSchool}
              onChange={handleSchoolFilter}
              style={{ width: 200, padding: '6px 12px', fontSize: 13 }}
            >
              <option value="">All Schools</option>
              {schools.map(s => (
                <option key={s.school_id} value={s.school_id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="btn-group">
            <button className="btn btn-secondary btn-sm" onClick={() => loadBackups()}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleTriggerAll}
              disabled={triggerLoading === 'all'}
            >
              <Database size={14} /> {triggerLoading === 'all' ? 'Running...' : 'Backup All'}
            </button>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>School</th>
                <th>Status</th>
                <th>Size</th>
                <th>Duration</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b, i) => (
                <tr key={i}>
                  <td><strong className="text-sm">{b.school_name || b.school_id}</strong></td>
                  <td><span className={`badge ${b.status}`}>{b.status}</span></td>
                  <td className="font-mono text-sm">{b.size_mb ? `${b.size_mb} MB` : '—'}</td>
                  <td className="text-sm text-secondary">{b.duration_seconds ? `${b.duration_seconds}s` : '—'}</td>
                  <td className="font-mono text-sm text-muted">
                    {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div className="btn-group">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleTrigger(b.id)}
                        disabled={!!triggerLoading}
                      >
                        <RefreshCw size={13} />
                      </button>
                      {b.download_url && (
                        <a href={b.download_url} className="btn btn-secondary btn-sm" download>
                          <Download size={13} />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {backups.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <Database size={40} />
                      <p>No backups yet</p>
                      <span className="sub">Backups run nightly or trigger one manually</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
